import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getSnapshotComparisonApplicationDetail,
  getSnapshotComparisonLabelSections,
  updateSnapshotComparisonSmeVerdict
} from '../../../api/snapshotChangeDetection';
import { DEFAULT_DETAIL_FILTERS } from '../constants';
import {
  LabelComparisonRecord,
  LabelSections,
  SnapshotComparisonDetailFilters,
  SnapshotComparisonDetailRow
} from '../types';
import {
  buildDistinctFilterOptions,
  matchesIndChangeFilter,
  matchesTierFilter,
  matchesVerdictFilter,
  normalizeDocumentId
} from '../utils';

/** Normalizes API records for the detail table. */
const mapRecordToRow = (record: LabelComparisonRecord): SnapshotComparisonDetailRow => ({
  ...record,
  current_document_id: normalizeDocumentId(record.current_document_id),
  compared_to_document_id: normalizeDocumentId(record.compared_to_document_id)
});

const EMPTY_LABEL_SECTIONS: LabelSections = { plr: [], non_plr: [] };

/** Loads label comparisons for one application; filters and SME edits are client-side. */
const useSnapshotComparisonDetail = (applicationNumber: string) => {
  const [allRows, setAllRows] = useState<SnapshotComparisonDetailRow[]>([]);
  const [sectionOptions, setSectionOptions] = useState<string[]>([]);
  const [filters, setFilters] = useState<SnapshotComparisonDetailFilters>(DEFAULT_DETAIL_FILTERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingRowId, setSavingRowId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);
  // Save reads the latest row from state without stale closures from DataGrid render.
  const allRowsRef = useRef(allRows);
  allRowsRef.current = allRows;

  const fetchDetail = useCallback(async () => {
    if (!applicationNumber.trim()) {
      setAllRows([]);
      setSectionOptions([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [detailResponse, labelSectionsResponse] = await Promise.all([
        getSnapshotComparisonApplicationDetail(applicationNumber),
        getSnapshotComparisonLabelSections()
      ]);

      if (labelSectionsResponse?.data?.status === 200) {
        const body: LabelSections = labelSectionsResponse.data.body ?? EMPTY_LABEL_SECTIONS;
        setSectionOptions(Array.isArray(body.plr) ? body.plr : []);
      } else {
        setSectionOptions([]);
      }

      if (detailResponse?.data?.status === 200) {
        const results: LabelComparisonRecord[] = detailResponse.data.body?.results ?? [];
        setAllRows(results.map(mapRecordToRow));
        return;
      }

      setAllRows([]);
      setError(`Unable to load details for application ${applicationNumber}.`);
    } catch {
      setAllRows([]);
      setSectionOptions([]);
      setError(`Unable to load details for application ${applicationNumber}.`);
    } finally {
      setLoading(false);
    }
  }, [applicationNumber]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const indChangeOptions = useMemo(
    () => buildDistinctFilterOptions(allRows.map(row => row.ind_change)),
    [allRows]
  );

  const tierOptions = useMemo(
    () => buildDistinctFilterOptions(allRows.map(row => row.tier)),
    [allRows]
  );

  // All detail filters run client-side.
  const filteredRows = useMemo(() => {
    return allRows.filter(
      row =>
        matchesVerdictFilter(row.verdict, filters.verdict) &&
        matchesIndChangeFilter(row.ind_change, filters.indChange) &&
        matchesTierFilter(row.tier, filters.tier)
    );
  }, [allRows, filters]);

  const updateRowField = useCallback(
    (
      id: number,
      field: 'sme_verdict' | 'sme_comments' | 'section_changed',
      value: boolean | string | string[] | null
    ) => {
      setAllRows(previous =>
        previous.map(row => (row.id === id ? { ...row, [field]: value } : row))
      );
    },
    []
  );

  const saveRow = useCallback(
    async (rowId: number) => {
      const row = allRowsRef.current.find(current => current.id === rowId);
      if (!row) {
        setFeedback({ severity: 'error', message: 'Unable to find the selected row.' });
        return;
      }

      if (typeof row.sme_verdict !== 'boolean') {
        setFeedback({ severity: 'error', message: 'Please select an SME verdict before saving.' });
        return;
      }

      setSavingRowId(row.id);
      setFeedback(null);

      try {
        const sectionChanged = (row.section_changed ?? [])
          .map(section => section.trim())
          .filter(Boolean);

        const response = await updateSnapshotComparisonSmeVerdict({
          label_id: row.id,
          sme_verdict: row.sme_verdict,
          sme_comments: row.sme_comments ?? null,
          section_changed: sectionChanged
        });

        if (response?.data?.status === 200) {
          // Refetch so UI matches any server-side normalization after save.
          await fetchDetail();
          setFeedback({ severity: 'success', message: 'SME review saved.' });
          return;
        }

        setFeedback({ severity: 'error', message: 'Failed to save SME review.' });
      } catch {
        setFeedback({ severity: 'error', message: 'Failed to save SME review.' });
      } finally {
        setSavingRowId(null);
      }
    },
    [fetchDetail]
  );

  const summary = useMemo(() => {
    // Counts use agent verdict; null verdict rows (e.g. escalated) are excluded from YES/NO.
    const yes = filteredRows.filter(row => row.verdict === true).length;
    const no = filteredRows.filter(row => row.verdict === false).length;
    return { total: filteredRows.length, yes, no, allTotal: allRows.length };
  }, [filteredRows, allRows.length]);

  const setVerdictFilter = (verdict: SnapshotComparisonDetailFilters['verdict']) => {
    setFilters(previous => ({ ...previous, verdict }));
  };

  const setIndChangeFilter = (indChange: string) => {
    setFilters(previous => ({ ...previous, indChange }));
  };

  const setTierFilter = (tier: string) => {
    setFilters(previous => ({ ...previous, tier }));
  };

  const clearFilters = () => {
    setFilters(DEFAULT_DETAIL_FILTERS);
  };

  const clearFeedback = useCallback(() => {
    setFeedback(null);
  }, []);

  return {
    rows: filteredRows,
    loading,
    error,
    savingRowId,
    feedback,
    summary,
    filters,
    indChangeOptions,
    tierOptions,
    sectionOptions,
    setVerdictFilter,
    setIndChangeFilter,
    setTierFilter,
    clearFilters,
    updateRowField,
    saveRow,
    clearFeedback
  };
};

export default useSnapshotComparisonDetail;
